'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface ComplianceItem {
  name: string;
  href: string;
  description: string;
}

interface ComplianceCategory {
  title: string;
  icon: string;
  items: ComplianceItem[];
}

const complianceData: ComplianceCategory[] = [
  {
    title: 'Security & Trust Certifications',
    icon: '🏆',
    items: [
      { name: 'ISO 27001', href: '/compliance', description: 'Information Security Management System certification. The global gold standard for information security.' },
      { name: 'SOC 2 (Type 1 & 2)', href: '/compliance', description: 'Service Organization Control 2 attestation. Demonstrate your security, availability, and confidentiality controls.' },
      { name: 'ISO 42001', href: '/compliance', description: 'AI Management System standard. Govern your AI systems responsibly and demonstrate trustworthy AI practices.' },
      { name: 'ISO 27017', href: '/compliance', description: 'Cloud security controls standard. Specific guidance for cloud service providers and cloud customers.' },
      { name: 'ISO 22301', href: '/compliance', description: 'Business Continuity Management System. Ensure your organization can continue operating during disruptions.' },
    ],
  },
  {
    title: 'Privacy & Data Protection',
    icon: '🔐',
    items: [
      { name: 'GDPR', href: '/compliance', description: 'General Data Protection Regulation compliance. Protect EU citizen data and avoid significant penalties.' },
      { name: 'CCPA', href: '/compliance', description: 'California Consumer Privacy Act compliance. Protect California resident data rights and privacy.' },
      { name: 'DPDPA', href: '/compliance', description: 'Digital Personal Data Protection Act compliance for organizations handling Indian citizen data.' },
      { name: 'ISO 27701', href: '/compliance', description: 'Privacy Information Management System. Extension to ISO 27001 specifically for privacy management.' },
      { name: 'ISO 27018', href: '/compliance', description: 'Protection of PII in public clouds. Code of practice for cloud service providers handling personal data.' },
    ],
  },
  {
    title: 'Industry & Regulatory Compliance',
    icon: '🏛️',
    items: [
      { name: 'PCI DSS', href: '/compliance', description: 'Payment Card Industry Data Security Standard. Mandatory for organizations that handle cardholder data.' },
      { name: 'HIPAA', href: '/compliance', description: 'Health Insurance Portability and Accountability Act. Required for healthcare organizations handling PHI.' },
      { name: 'FedRAMP', href: '/compliance', description: 'Federal Risk and Authorization Management Program. Required for cloud services used by US federal agencies.' },
      { name: 'CMMC', href: '/compliance', description: 'Cybersecurity Maturity Model Certification. Required for US Department of Defense contractors.' },
      { name: 'SOX ITGC', href: '/compliance', description: 'Sarbanes-Oxley IT General Controls. Required for publicly traded companies to ensure financial reporting integrity.' },
      { name: 'NIST CSF 2.0', href: '/compliance', description: 'NIST Cybersecurity Framework. Voluntary framework for managing and reducing cybersecurity risk.' },
    ],
  },
  {
    title: 'Audit & Advisory Services',
    icon: '📊',
    items: [
      { name: 'CERT-In Security Audit', href: '/compliance', description: 'Mandatory security audit for organizations under CERT-In guidelines. We are CERT-In empanelled.' },
      { name: 'Cloud Security Audit', href: '/compliance', description: 'Comprehensive audit of your cloud environment against security best practices and compliance requirements.' },
      { name: 'ITGC Assessment', href: '/compliance', description: 'IT General Controls assessment covering access management, change management, and operations.' },
      { name: 'vCISO/vGRC/vDPO', href: '/compliance', description: 'Virtual CISO, GRC, and Data Protection Officer services. Expert guidance without the full-time cost.' },
      { name: 'Cyber Crisis Management Plan', href: '/compliance', description: 'Develop and test your incident response and crisis management capabilities.' },
      { name: 'OT Security Assessment', href: '/compliance', description: 'Specialized assessment for operational technology environments against IEC 62443 and NERC CIP.' },
    ],
  },
  {
    title: 'Frameworks & Standards',
    icon: '📍',
    items: [
      { name: 'NIST 800-53', href: '/compliance', description: 'Security and privacy controls for federal information systems. Comprehensive control catalog.' },
      { name: 'NIST 800-171', href: '/compliance', description: 'Protecting Controlled Unclassified Information in non-federal systems.' },
      { name: 'CIS Controls', href: '/compliance', description: 'Center for Internet Security Controls. Prioritized set of actions to protect against cyber attacks.' },
      { name: 'ISO 27002', href: '/compliance', description: 'Code of practice for information security controls. Detailed guidance for implementing ISO 27001.' },
    ],
  },
];

export default function CompliancePage() {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }}>
      <Navbar />

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #07182a 0%, #0a1d2f 50%, #0d2640 100%)',
        paddingTop: '140px',
        paddingBottom: '80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(34,197,94,0.07) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div style={{ maxWidth: '90%', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '100px', padding: '6px 16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: 500 }}>20+ Compliance Frameworks Supported</span>
          </div>
          <h1 style={{ fontSize: '48px', fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: '0 0 20px' }}>
            Your Security Work Becomes{' '}
            <span style={{ color: '#22c55e' }}>Compliance Evidence</span>
            {' '}— Automatically.
          </h1>
          <p style={{ fontSize: '18px', color: '#9ca3af', maxWidth: '700px', margin: '0 auto 32px', lineHeight: '1.7' }}>
            You shouldn&apos;t have to do the same work twice. TechLabs turns your security assessment results into audit-ready evidence for every framework your customers and regulators require.
          </p>
          <Link href="/contact" style={{ display: 'inline-flex', alignItems: 'center', padding: '14px 36px', background: '#22c55e', borderRadius: '100px', color: '#fff', textDecoration: 'none', fontSize: '17px', fontWeight: 600 }} className="hover:bg-green-600">
            Get Compliance Audit
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
              One Assessment, <span style={{ color: '#22c55e' }}>Multiple Certifications</span>
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
              Our integrated approach means your security testing automatically generates evidence for multiple compliance frameworks simultaneously.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
            {[
              { step: '01', icon: '🔍', title: 'Security Assessment', desc: 'We conduct comprehensive security testing across your environment.' },
              { step: '02', icon: '📊', title: 'Evidence Collection', desc: 'Findings are automatically mapped to compliance control requirements.' },
              { step: '03', icon: '📝', title: 'Gap Analysis', desc: 'We identify gaps between your current state and compliance requirements.' },
              { step: '04', icon: '✅', title: 'Remediation Support', desc: 'Our experts guide you through closing gaps with prioritized recommendations.' },
              { step: '05', icon: '🏆', title: 'Audit Readiness', desc: 'Receive audit-ready documentation and verifiable certificates.' },
            ].map((item) => (
              <div key={item.step} style={{ textAlign: 'center', padding: '28px 20px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>{item.icon}</div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#22c55e', background: '#dcfce7', padding: '2px 10px', borderRadius: '100px', display: 'inline-block', marginBottom: '10px' }}>{item.step}</div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 8px' }}>{item.title}</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance categories */}
      <section style={{ background: '#f9fafb', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
              Compliance <span style={{ color: '#22c55e' }}>Frameworks We Support</span>
            </h2>
          </div>

          {/* Category tabs */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '40px', justifyContent: 'center' }}>
            {complianceData.map((cat, i) => (
              <button
                key={cat.title}
                onClick={() => setActiveCategory(i)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: activeCategory === i ? '#fff' : '#1a1a1a',
                  background: activeCategory === i ? 'linear-gradient(96deg, #07182a -70%, #000 4%, #1a5c2a 141%, #22c55e 148%)' : '#fff',
                  border: activeCategory === i ? '1px solid transparent' : '1px solid #0a1d2f',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>{cat.icon}</span> {cat.title}
              </button>
            ))}
          </div>

          {/* Items grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {complianceData[activeCategory].items.map((item) => (
              <div
                key={item.name}
                style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '24px', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                className="hover:border-green-300 hover:shadow-md"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '16px' }}>→</span>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{item.name}</h3>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: '0 0 16px' }}>{item.description}</p>
                <Link href="/contact" style={{ fontSize: '13px', color: '#22c55e', fontWeight: 600, textDecoration: 'none' }}>Get Started →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
            Our <span style={{ color: '#22c55e' }}>Credentials</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '600px', margin: '0 auto 48px' }}>
            TechLabs is certified, attested, and empanelled by the world&apos;s leading security and compliance bodies.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px' }}>
            {[
              { cert: 'ISO 27001:2022', desc: 'Information Security' },
              { cert: 'ISO 27017:2015', desc: 'Cloud Security' },
              { cert: 'ISO 27018:2019', desc: 'Privacy in Cloud' },
              { cert: 'SOC 2 Type II', desc: 'Trust Services' },
              { cert: 'GDPR Compliant', desc: 'Data Protection' },
              { cert: 'CERT-In Empanelled', desc: 'Govt. Recognized' },
            ].map((item) => (
              <div key={item.cert} style={{ background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '12px', padding: '24px 28px', textAlign: 'center', minWidth: '160px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#22c55e', marginBottom: '6px' }}>{item.cert}</div>
                <div style={{ fontSize: '12px', color: '#9ca3af' }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: '#0b0e14', padding: '80px 0' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center', padding: '0 5%' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>Ready to Achieve Compliance?</h2>
          <p style={{ fontSize: '16px', color: '#9ca3af', margin: '0 0 32px', lineHeight: '1.6' }}>
            Let our compliance experts guide you through the certification process. We make compliance straightforward and efficient.
          </p>
          <Link href="/contact" style={{ display: 'inline-flex', alignItems: 'center', padding: '14px 36px', background: '#22c55e', borderRadius: '100px', color: '#fff', textDecoration: 'none', fontSize: '17px', fontWeight: 600 }} className="hover:bg-green-600">
            Get Compliance Audit
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
